import {
  CalculatorRate,
  ContentBlock,
  DocumentItem,
  Job,
  NewsItem,
  Page,
  PublicContent,
  PublicPageResponse,
  Service,
  SiteSettings,
  Statistic,
} from '../types/content';
import { validateStorageTariff } from './storage-tariff-model';

const API_BASE =
  process.env.REACT_APP_API_BASE_URL || 'http://localhost:5194/api';

export const SERVER_BASE = API_BASE.replace(/\/api\/?$/, '');

export { submitApplication, submitContact } from './forms';
export type { ContactPayload, FormSuccess } from './forms';

/** Port_Api dil filtresi: TR | EN, ALL tum dilleri dondurur (yalnizca /icerikler). */
export type ContentLanguage = 'TR' | 'EN' | 'ALL';

export type ContentQuery = {
  /** Bos gonderilirse Port_Api TR varsayar. */
  dil?: ContentLanguage;
  /**
   * EN kayitlarinin kendi dokumani yoksa TR ikizinin dokumanlarini kullanmasi
   * (Port_Api varsayilani true). Yalnizca false ise parametre gonderilir.
   */
  dokumanFallback?: boolean;
};

/** /icerikler sorgu dizesini kurar; tip gibi ek filtreler extra ile eklenir. */
function contentPath(options?: ContentQuery, extra?: Record<string, string>) {
  const params = new URLSearchParams({ includeInactive: 'false', ...(extra || {}) });
  if (options?.dil) params.set('dil', options.dil);
  if (options?.dokumanFallback === false) params.set('dokumanFallback', 'false');
  return `/icerikler?${params.toString()}`;
}

async function apiRequest<T>(path: string, init?: RequestInit): Promise<T> {
  const isFormData = init?.body instanceof FormData;
  const headers: Record<string, string> = {
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    ...(init?.headers as Record<string, string> | undefined),
  };

  let response: Response;
  try {
    response = await fetch(`${API_BASE}${path}`, { ...init, headers });
  } catch {
    throw new Error('API sunucusuna baglanilamadi. Port_Api calisiyor mu?');
  }

  if (!response.ok) {
    let message = 'Islem basarisiz';
    try {
      const body = await response.json();
      message = body.message || message;
    } catch {
      // yut
    }
    throw new Error(message);
  }

  return (await response.json()) as T;
}

/** Ardiye hizmetleri ve gun bazli fiyatlar portalda yonetilen tek tarifeden okunur. */
export async function fetchStorageTariff(signal?: AbortSignal) {
  const value = await apiRequest<unknown>('/storage-tariff', {
    signal,
    cache: 'no-store',
    credentials: 'omit',
  });
  return validateStorageTariff(value);
}

type IcerikDokuman = {
  id?: number;
  icerikId?: number;
  dosyaAdi?: string | null;
  contentType?: string | null;
  dosyaVerisi?: string | null;
  kategori?: string | null;
  sira?: number;
  aktif?: boolean;
  gosterUrl?: string | null;
};

type IcerikItem = {
  id: number;
  tip: string;
  baslik: string;
  /** Icerik.Dil kolonu: TR | EN */
  dil?: string | null;
  aciklama?: string | null;
  tarih?: string | null;
  gorsel?: string | null;
  bannerKapakResmi?: string | null;
  link?: string | null;
  ekstra?: string | null;
  sira?: number;
  aktif?: boolean;
  baslangicTarihi?: string | null;
  bitisTarihi?: string | null;
  eklemeTarihi?: string | null;
  guncellemeTarihi?: string | null;
  dokumanlar?: IcerikDokuman[] | null;
};

type IcerikExtra = {
  slug?: string;
  category?: string;
  categories?: string[];
  placements?: string[];
  visibility?: 'categoryOnly' | 'allAreas';
  content?: string;
  imageUrl?: string;
  isFeatured?: boolean;
  isPublished?: boolean;
  recordType?: string;
  pageSlug?: string;
  blockKey?: string;
  title?: string;
  subtitle?: string;
  items?: string[] | Record<string, unknown>[];
  features?: string[];
  label?: string;
  value?: string;
  statsCategory?: string;
  heroTitle?: string;
  heroSubtitle?: string;
  heroImage?: string;
  heroCtaText?: string;
  heroCtaLink?: string;
  department?: string;
  location?: string;
  employmentType?: string;
  requirements?: string;
  icon?: string;
  // Anasayfa dokuman karti (recordType: homeDocument)
  group?: string;
  subLabel?: string;
  fileUrl?: string;
  fileName?: string;
  contentType?: string;
  rateKey?: string;
  unit?: string;
  description?: string;
  meta?: {
    type?: string;
    index?: number;
    columnIndex?: number;
    linkIndex?: number;
  };
};

const PAGE_SORT_ORDER: Record<string, number> = {
  anasayfa: 0,
  hizmetler: 1,
  hakkimizda: 2,
  'liman-ozellikleri': 3,
  haberler: 4,
  surdurulebilirlik: 5,
  kariyer: 6,
  iletisim: 7,
};

function parseExtra(raw?: string | null): IcerikExtra {
  if (!raw) return {};
  try {
    return JSON.parse(raw) as IcerikExtra;
  } catch {
    return {};
  }
}

function normalizeText(value?: string | null) {
  return (value || '').trim();
}

function simplify(value?: string | null) {
  return normalizeText(value)
    .toLocaleLowerCase('tr-TR')
    .replace(/ı/g, 'i')
    .replace(/İ/g, 'i')
    .replace(/ö/g, 'o')
    .replace(/ü/g, 'u')
    .replace(/ş/g, 's')
    .replace(/ç/g, 'c')
    .replace(/ğ/g, 'g');
}

function slugify(value?: string | null) {
  return simplify(value)
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function normalizePageSlug(value?: string | null) {
  const slug = slugify(value);
  if (!slug) return undefined;
  if (slug === 'hizmetler' || slug.startsWith('hizmetler')) return 'hizmetler';
  if (slug.includes('anasayfa') || slug.includes('home')) return 'anasayfa';
  if (slug === 'operasyon' || slug.startsWith('operasyon')) return 'anasayfa';
  if (slug.includes('hakkimizda')) return 'hakkimizda';
  if (slug.includes('liman') || slug.includes('altyapi') || slug.includes('ozellik')) return 'liman-ozellikleri';
  if (slug.includes('haber')) return 'haberler';
  if (slug.includes('surdur')) return 'surdurulebilirlik';
  if (slug.includes('kariyer')) return 'kariyer';
  if (slug.includes('iletis')) return 'iletisim';
  return slug;
}

function stripNumberSuffix(value?: string | null) {
  return normalizeText(value).replace(/\s+\d+$/, '');
}

function parseItemList(value?: string | null) {
  const raw = normalizeText(value);
  if (!raw) return undefined;
  // Portal cok satirli alanlari kaydederken duz "\n" metni de yazabiliyor.
  const parts = raw
    .split(/\\n|\r?\n|[;|]/)
    .map((item) => item.trim())
    .filter(Boolean);
  return parts.length > 1 ? parts : undefined;
}

function readObjectString(value: unknown) {
  return typeof value === 'string' ? normalizeText(value) : '';
}

function dedupeByLatestId<T extends { id?: number; sortOrder: number }>(
  items: T[],
  getKey: (item: T) => string,
) {
  const map = new Map<string, T>();

  items.forEach((item) => {
    const key = getKey(item);
    const current = map.get(key);
    if (!current) {
      map.set(key, item);
      return;
    }

    const currentId = current.id ?? 0;
    const nextId = item.id ?? 0;
    if (nextId >= currentId) {
      map.set(key, item);
    }
  });

  return Array.from(map.values()).sort((a, b) => a.sortOrder - b.sortOrder);
}

function documentUrl(doc: IcerikDokuman) {
  if (doc.dosyaVerisi && doc.contentType) {
    return `data:${doc.contentType};base64,${doc.dosyaVerisi}`;
  }
  if (doc.gosterUrl) {
    if (/^https?:\/\//.test(doc.gosterUrl)) return doc.gosterUrl;
    return `${SERVER_BASE}${doc.gosterUrl.startsWith('/') ? '' : '/'}${doc.gosterUrl}`;
  }
  if (doc.id) {
    return `${SERVER_BASE}/api/dokumanlar/${doc.id}/goster`;
  }
  return '#';
}

function mapDocuments(docs?: IcerikDokuman[] | null): DocumentItem[] {
  return (docs || [])
    .filter((doc) => doc.aktif !== false)
    .map((doc, index) => ({
      id: doc.id || index + 1,
      title: doc.dosyaAdi || `Dokuman ${index + 1}`,
      category: doc.kategori || doc.contentType || undefined,
      fileUrl: documentUrl(doc),
      icon: doc.contentType || undefined,
      sortOrder: doc.sira ?? index,
      isPublished: doc.aktif ?? true,
    }))
    .sort((a, b) => a.sortOrder - b.sortOrder);
}

function shouldPromoteDocument(doc: DocumentItem) {
  const category = simplify(doc.category);
  const title = simplify(doc.title);
  const contentType = simplify(doc.icon);
  return (
    category.includes('sertifika') ||
    category.includes('tarife') ||
    category.includes('rehber') ||
    category.includes('politika') ||
    category.includes('bulten') ||
    category.includes('video') ||
    contentType.includes('video') ||
    /\.mp4$/i.test(doc.title) ||
    title.includes('rezervasyon') ||
    title.includes('plaka') ||
    category.includes('kvkk') ||
    category.includes('hukuki') ||
    title.includes('kvkk')
  );
}

function firstImage(documents: DocumentItem[]) {
  return documents.find((doc) => {
    const isImageType = doc.icon?.startsWith('image/') || doc.category?.startsWith('image/');
    const isImageName = /\.(png|jpe?g|gif|webp|svg|bmp)$/i.test(doc.title || '');
    return (isImageType || isImageName) && doc.fileUrl && doc.fileUrl !== '#';
  })?.fileUrl;
}

function itemImage(item: IcerikItem, documents: DocumentItem[] = []) {
  return item.bannerKapakResmi || item.gorsel || firstImage(documents);
}

function ensurePage(
  pages: Map<string, Page>,
  slug: string,
  title?: string,
) {
  const existing = pages.get(slug);
  if (existing) return existing;

  const page: Page = {
    id: PAGE_SORT_ORDER[slug] ?? pages.size + 1,
    slug,
    title: title || slug,
    sortOrder: PAGE_SORT_ORDER[slug] ?? pages.size,
    isPublished: true,
  };
  pages.set(slug, page);
  return page;
}

function pageFieldKey(value?: string | null) {
  const text = simplify(value);
  if (!text.includes('hero')) return undefined;
  if (text.includes('baslik')) return 'heroTitle';
  if (text.includes('aciklama') || text.includes('subtitle') || text.includes('alt baslik')) return 'heroSubtitle';
  if (text.includes('gorsel') || text.includes('gorsel')) return 'heroImage';
  if (text.includes('buton metni')) return 'heroCtaText';
  if (text.includes('buton linki')) return 'heroCtaLink';
  return undefined;
}

function inferBlockKey(pageSlug: string, title: string) {
  const text = simplify(title);

  if (pageSlug === 'hizmetler') {
    const serviceMatch = text.match(/^(\d+)\s*hizmet\s*-\s*(.+?)\s*-\s*(ust etiket|baslik|aciklama|gorsel url|ozellikler)$/);
    if (serviceMatch) {
      const order = serviceMatch[1];
      const serviceSlug = slugify(serviceMatch[2]);
      const fieldMap: Record<string, string> = {
        'ust etiket': 'ust-etiket',
        baslik: 'baslik',
        aciklama: 'aciklama',
        'gorsel url': 'gorsel-url',
        ozellikler: 'ozellikler',
      };
      return `${order}-hizmet-${serviceSlug}-${fieldMap[serviceMatch[3]]}`;
    }

    if (text.includes('online hizmetler') && text.includes('ust etiket')) return 'services_online_eyebrow';
    if (text.includes('online hizmetler') && text.includes('baslik')) return 'services_online_title';
    if (text.includes('online hizmetler') && text.includes('aciklama')) return 'services_online_description';

    const onlineCardMatch = text.match(/^online kart\s*(\d+)\s*-\s*(ikon url|buton linki|ozellikler|baslik|buton metni|alt etiket|rozet|aciklama)$/);
    if (onlineCardMatch) {
      const fieldMap: Record<string, string> = {
        'ikon url': 'icon-url',
        'buton linki': 'button-link',
        ozellikler: 'features',
        baslik: 'title',
        'buton metni': 'button-text',
        'alt etiket': 'footer-label',
        rozet: 'badge',
        aciklama: 'description',
      };
      return `services_online_card_${onlineCardMatch[1]}_${fieldMap[onlineCardMatch[2]]}`;
    }
  }

  if (pageSlug === 'anasayfa') {
    if (text.includes('dokuman') && text.endsWith('baslik')) return 'home_docs_title';
    if (text.includes('dokuman') && text.endsWith('aciklama')) return 'home_docs_description';
    if (text.includes('dokuman') && text.includes('ust serit baslik')) return 'home_docs_strip_title';
    if (text.includes('dokuman') && text.includes('ust serit deger 1')) return 'home_docs_strip_stat_1_value';
    if (text.includes('dokuman') && text.includes('ust serit aciklama 1')) return 'home_docs_strip_stat_1_label';
    if (text.includes('dokuman') && text.includes('ust serit deger 2')) return 'home_docs_strip_stat_2_value';
    if (text.includes('dokuman') && text.includes('ust serit aciklama 2')) return 'home_docs_strip_stat_2_label';

    if (text.includes('stratejik konum') && text.endsWith('ust etiket')) return 'home_location_eyebrow';
    if (text.includes('stratejik konum') && text.endsWith('baslik')) return 'home_location_title';
    if (text.includes('stratejik konum') && text.endsWith('aciklama')) return 'home_location_description';
    if (text.includes('stratejik konum') && text.includes('koordinatlar baslik')) return 'home_location_coordinates_title';
    if (text.includes('stratejik konum') && text.includes('koordinatlar metin')) return 'home_location_coordinates_text';
    if (text.includes('stratejik konum') && text.includes('adres baslik')) return 'home_location_address_title';
    if (text.includes('stratejik konum') && text.includes('adres metin')) return 'home_location_address_text';
    if (text.includes('stratejik konum') && text.includes('mesafe baslik')) return 'home_location_distance_title';
    if (text.includes('stratejik konum') && text.includes('mesafe metin')) return 'home_location_distance_text';
    if (text.includes('stratejik konum') && text.includes('ulasim metin')) return 'home_location_access_text';
  }

  if (pageSlug === 'anasayfa' || pageSlug === 'hizmetler') {
    if (pageSlug === 'anasayfa' && text.includes('hero') && text.includes('ust yazi')) {
      return 'home_hero_eyebrow';
    }
    if (text.includes('ust etiket')) return pageSlug === 'hizmetler' ? 'operations_intro_eyebrow' : 'operations_eyebrow';
    if (text.endsWith('baslik') && !text.includes('kutucuk')) {
      return pageSlug === 'hizmetler' ? 'operations_intro_title' : 'operations_title';
    }
    if (text.endsWith('aciklama') && !text.includes('kutucuk')) {
      return pageSlug === 'hizmetler' ? 'operations_intro_description' : 'operations_description';
    }
    if (text.includes('buton metni')) {
      return pageSlug === 'hizmetler' ? 'operations_intro_cta_text' : 'operations_button_text';
    }
    if (text.includes('buton linki')) {
      return pageSlug === 'hizmetler' ? 'operations_intro_cta_link' : 'operations_button_link';
    }
  }

  if (pageSlug === 'surdurulebilirlik') {
    if (text.includes('cevresel') || text.includes('environment')) return 'esg_environmental';
    if (text.includes('sosyal')) return 'esg_social';
    if (text.includes('yonetisim') || text.includes('governance')) return 'esg_governance';
    if (text.includes('odak') || text.includes('ska')) return 'focus_areas';
    if (text.includes('metrik') && text.includes('su')) return 'metric_water';
    if (text.includes('metrik') && text.includes('atik')) return 'metric_waste';
    if (text.includes('metrik') && text.includes('enerji')) return 'metric_energy';
    if (text.includes('metrik') && text.includes('iso')) return 'metric_iso';
  }

  if (pageSlug === 'liman-ozellikleri') {
    if (text.includes('hero') && text.includes('ust etiket')) return 'infrastructure_hero_eyebrow';
    if (text.includes('hero') && text.includes('baslik')) return 'infrastructure_hero_title';
    if (text.includes('hero') && text.includes('aciklama')) return 'infrastructure_hero_description';
    if (text.includes('hizmet') && text.includes('ust etiket')) return 'infrastructure_services_eyebrow';
    if (text.includes('hizmet') && text.includes('baslik')) return 'infrastructure_services_title';
    if (text.includes('hizmet') && text.includes('alan')) return 'infrastructure_services_intro';
    if (text.includes('genel') && text.includes('ust etiket')) return 'infrastructure_overview_eyebrow';
    if (text.includes('genel') && text.includes('baslik')) return 'infrastructure_overview_title';
    if (text.includes('genel') && text.includes('aciklama')) return 'infrastructure_overview_description';
    if (text.includes('genel') && text.includes('pillar')) return 'infrastructure_overview_pills';
    if (text.includes('genel') && text.includes('sol')) return 'infrastructure_overview_list_left';
    if (text.includes('genel') && text.includes('sag')) return 'infrastructure_overview_list_right';
    if (text.includes('genel') && text.includes('gorsel')) return 'infrastructure_overview_image';
    if (text.includes('iskele') && text.includes('ust etiket')) return 'infrastructure_berth_eyebrow';
    if (text.includes('iskele') && text.includes('baslik')) return 'infrastructure_berth_title';
    if (text.includes('iskele') && text.includes('aciklama')) return 'infrastructure_berth_description';
    if (text.includes('iskele') && text.includes('kart') && text.includes('1')) return 'infrastructure_berth_card_1';
    if (text.includes('iskele') && text.includes('kart') && text.includes('2')) return 'infrastructure_berth_card_2';
    if (text.includes('ekipman') && text.includes('ust etiket')) return 'infrastructure_equipment_eyebrow';
    if (text.includes('ekipman') && text.includes('baslik')) return 'infrastructure_equipment_title';
    if (text.includes('ekipman') && text.includes('aciklama')) return 'infrastructure_equipment_description';
    if (text.includes('ekipman') && text.includes('liste')) return 'infrastructure_equipment_items';
    if (text.includes('gumruk') && text.includes('ust etiket')) return 'infrastructure_customs_eyebrow';
    if (text.includes('gumruk') && text.includes('baslik')) return 'infrastructure_customs_title';
    if (text.includes('gumruk') && text.includes('aciklama')) return 'infrastructure_customs_description';
    if (text.includes('gumruk') && text.includes('pillar')) return 'infrastructure_customs_pills';
    if (text.includes('gumruk') && text.includes('ozellik')) return 'infrastructure_customs_features';
    if (text.includes('gumruk') && text.includes('gorsel')) return 'infrastructure_customs_image';
    if (text.includes('gumruk') && text.includes('rozet')) return 'infrastructure_customs_badge';
    if (text.includes('x-ray') && text.includes('ust etiket')) return 'infrastructure_xray_eyebrow';
    if (text.includes('x-ray') && text.includes('baslik')) return 'infrastructure_xray_title';
    if (text.includes('x-ray') && text.includes('aciklama')) return 'infrastructure_xray_description';
    if (text.includes('x-ray') && text.includes('pillar')) return 'infrastructure_xray_pills';
    if (text.includes('x-ray') && text.includes('ozellik')) return 'infrastructure_xray_features';
    if (text.includes('x-ray') && text.includes('gorsel')) return 'infrastructure_xray_image';
    if (text.includes('x-ray') && text.includes('rozet')) return 'infrastructure_xray_badge';
  }

  if (pageSlug === 'hakkimizda') {
    if (text.includes('hero') && text.includes('ust etiket')) return 'about_hero_eyebrow';
    if (text.includes('hero') && text.includes('baslik')) return 'about_hero_title';
    if (text.includes('hero') && text.includes('aciklama')) return 'about_hero_description';
    if (text.includes('kurumsal') && text.includes('ust etiket')) return 'about_corporate_eyebrow';
    if (text.includes('kurumsal') && text.includes('baslik')) return 'about_corporate_title';
    if (text.includes('kurumsal') && text.includes('aciklama')) return 'about_corporate_description';
    if (text.includes('kurumsal') && text.includes('gorsel')) return 'about_corporate_image';
    if (text.includes('deger') && text.includes('ust etiket')) return 'about_values_eyebrow';
    if (text.includes('deger') && text.includes('baslik')) return 'about_values_title';
    if (text.includes('deger') && text.includes('aciklama')) return 'about_values_description';
    if (text.includes('vizyon')) return 'vision';
    if (text.includes('misyon')) return 'mission';
    if (text.includes('deger')) return 'values';
  }

  return slugify(title) || `${pageSlug}-block`;
}

const FOOTER_FIELD_ORDER = [
  'siteName',
  'slogan',
  'logo',
  'topbarAddress',
  'onlineServicesUrl',
  'footerAddress',
  'footerPhone',
  'footerEmail',
  'footerNote',
  'newsletterText',
  'mapEmbedUrl',
  'mapLat',
  'mapLng',
  'mapAddress',
  'defaultLanguage',
  'copyrightText',
  // Portal SITE_SETTINGS_FIELD_ORDER ile ayni sirada olmali (sira = -2000 - indeks)
  'googleMapsApiKey',
] as const;

const FOOTER_SECTION_BASE = {
  field: -2000,
  social: -2100,
  footerColumn: -2200,
  footerLink: -2300,
  navigation: -2400,
  quickLink: -2500,
  language: -2600,
} as const;

function compactFooterArray<T>(items: Array<T | undefined | null>): T[] {
  return items.filter((item): item is T => item != null);
}

function mapFooterSettings(items: IcerikItem[]): Partial<SiteSettings> {
  const settings: Partial<SiteSettings> = {
    socialLinks: [],
    navigation: [],
    quickLinks: [],
    languages: [],
    footerColumns: [],
  };

  const footerItems = items
    .filter((item) => item.aktif !== false && simplify(item.tip) === 'footer')
    .sort((a, b) => (a.sira ?? 0) - (b.sira ?? 0));

  for (const item of footerItems) {
    const title = normalizeText(item.baslik);
    const titleSimple = simplify(title);
    const text = normalizeText(item.aciklama);
    const link = normalizeText(item.link);
    const sira = item.sira ?? 0;
    const extra = parseExtra(item.ekstra);
    const metaType = simplify(extra.meta?.type);
    const columnIndex = extra.meta?.columnIndex ?? extra.meta?.index ?? 0;
    const linkIndex = extra.meta?.linkIndex ?? 0;

    // Direct mapping by title for portal footer records
    if (titleSimple.includes('footer site adi') && text) {
      settings.siteName = text;
      continue;
    }
    if (titleSimple.includes('footer slogan') && text) {
      settings.footerNote = text;
      settings.slogan = settings.slogan || text;
      continue;
    }
    if ((titleSimple.includes('footer telif metni') || titleSimple.includes('footer telif')) && text) {
      settings.copyrightText = text;
      continue;
    }
    if (titleSimple.includes('footer adres') && text) {
      settings.footerAddress = text;
      settings.topbarAddress = settings.topbarAddress || text;
      settings.mapAddress = settings.mapAddress || text;
      continue;
    }
    if (titleSimple.includes('footer telefon') && text) {
      settings.footerPhone = text;
      continue;
    }
    if ((titleSimple.includes('footer e-posta') || titleSimple.includes('footer eposta') || titleSimple.includes('footer email')) && text) {
      settings.footerEmail = text;
      continue;
    }
    if (titleSimple.includes('footer logo') && (text || link)) {
      settings.logo = text || link;
      continue;
    }

    // Direct mapping by row metadata for portal footer grids
    if (metaType === 'footercolumn') {
      settings.footerColumns ||= [];
      const current = settings.footerColumns[columnIndex] || { title: '', links: [] };
      settings.footerColumns[columnIndex] = {
        title: text || title || current.title,
        links: current.links || [],
      };
      continue;
    }

    if (metaType === 'footerlink') {
      settings.footerColumns ||= [];
      const current = settings.footerColumns[columnIndex] || { title, links: [] };
      const links = [...(current.links || [])];
      links[linkIndex] = {
        label: text || '',
        path: link || '/',
      };
      settings.footerColumns[columnIndex] = {
        title: current.title || title,
        links,
      };
      continue;
    }

    // Scalar fields by sort (portal Site Settings)
    if (sira <= FOOTER_SECTION_BASE.field && sira > FOOTER_SECTION_BASE.social) {
      const offset = FOOTER_SECTION_BASE.field - sira;
      const field = FOOTER_FIELD_ORDER[offset];
      if (field && text) {
        (settings as Record<string, unknown>)[field] = text;
        if (field === 'footerAddress') {
          settings.topbarAddress = settings.topbarAddress || text;
          settings.mapAddress = settings.mapAddress || text;
        }
        continue;
      }
    }

    // Social
    if (sira <= FOOTER_SECTION_BASE.social && sira > FOOTER_SECTION_BASE.footerColumn) {
      const index = FOOTER_SECTION_BASE.social - sira;
      settings.socialLinks ||= [];
      settings.socialLinks[index] = {
        platform: text || `Platform ${index + 1}`,
        url: link || '',
      };
      continue;
    }

    // Footer column titles
    if (sira <= FOOTER_SECTION_BASE.footerColumn && sira > FOOTER_SECTION_BASE.footerLink) {
      const index = FOOTER_SECTION_BASE.footerColumn - sira;
      settings.footerColumns ||= [];
      const current = settings.footerColumns[index] || { title: '', links: [] };
      settings.footerColumns[index] = {
        title: text || title.replace(/^Footer Sutun - /i, '').trim() || current.title,
        links: current.links || [],
      };
      continue;
    }

    // Footer links
    if (sira <= FOOTER_SECTION_BASE.footerLink && sira > FOOTER_SECTION_BASE.navigation) {
      const offset = FOOTER_SECTION_BASE.footerLink - sira;
      const columnIndex = Math.floor(offset / 100);
      const linkIndex = offset % 100;
      settings.footerColumns ||= [];
      const current = settings.footerColumns[columnIndex] || { title: '', links: [] };
      const links = [...(current.links || [])];
      links[linkIndex] = {
        label: text || title.replace(/^Footer Link - /i, '').trim() || '',
        path: link || '',
      };
      settings.footerColumns[columnIndex] = { ...current, links };
      continue;
    }

    // Navigation
    if (sira <= FOOTER_SECTION_BASE.navigation && sira > FOOTER_SECTION_BASE.quickLink) {
      const index = FOOTER_SECTION_BASE.navigation - sira;
      settings.navigation ||= [];
      settings.navigation[index] = { label: text || '', path: link || '' };
      continue;
    }

    // Quick links
    if (sira <= FOOTER_SECTION_BASE.quickLink && sira > FOOTER_SECTION_BASE.language) {
      const index = FOOTER_SECTION_BASE.quickLink - sira;
      settings.quickLinks ||= [];
      settings.quickLinks[index] = { label: text || '', path: link || '' };
      continue;
    }

    // Languages
    if (sira <= FOOTER_SECTION_BASE.language && sira > FOOTER_SECTION_BASE.language - 100) {
      const index = FOOTER_SECTION_BASE.language - sira;
      settings.languages ||= [];
      settings.languages[index] = { code: text || '', label: link || text || '' };
      continue;
    }

    // Legacy heuristic fallback (eski "Footer" baslikli kayitlar)
    if (titleSimple === 'footer' || !titleSimple) {
      if (link && /linkedin/i.test(text || link)) {
        settings.socialLinks = [
          ...(settings.socialLinks || []).filter((entry) => entry.platform !== 'LinkedIn'),
          { platform: 'LinkedIn', url: link },
        ];
        continue;
      }
      if (link && /twitter|x\.com|youtube/i.test(text || link)) {
        settings.socialLinks = [
          ...(settings.socialLinks || []).filter((entry) => entry.platform !== 'Twitter'),
          { platform: 'Twitter', url: link },
        ];
        continue;
      }
      if (text.includes('@')) {
        settings.footerEmail = text;
        continue;
      }
      if (/^\+?\d/.test(text)) {
        settings.footerPhone = text;
        continue;
      }
      if (/adana|yumurtalik|sugozu/i.test(simplify(text))) {
        settings.footerAddress = text;
        settings.topbarAddress = settings.topbarAddress || text;
        settings.mapAddress = settings.mapAddress || text;
        continue;
      }
      if (text) settings.footerNote = text;
    }
  }

  settings.socialLinks = compactFooterArray(settings.socialLinks || []);
  settings.navigation = compactFooterArray(settings.navigation || []);
  settings.quickLinks = compactFooterArray(settings.quickLinks || []);
  settings.languages = compactFooterArray(settings.languages || []);
  settings.footerColumns = compactFooterArray(settings.footerColumns || []).map((column) => ({
    title: column.title,
    links: compactFooterArray(column.links || []),
  }));

  return settings;
}

function mapNewsItem(item: IcerikItem): NewsItem {
  const extra = parseExtra(item.ekstra);
  const documents = mapDocuments(item.dokumanlar);
  const categories = Array.isArray(extra.categories)
    ? extra.categories.filter(Boolean)
    : extra.category
      ? [extra.category]
      : [];
  const placements = Array.isArray(extra.placements) ? extra.placements.filter(Boolean) : [];

  return {
    id: item.id,
    // Baslik iki dilde de Turkce anahtar olarak tutuluyor (slug ve eslesme icin);
    // gorunen baslik varsa Ekstra.title'dan alinir, yoksa ceviri sozlugune duser.
    title: normalizeText(extra.title) || item.baslik,
    slug: extra.slug || slugify(item.baslik) || String(item.id),
    summary: normalizeText(item.aciklama) || '',
    content: extra.content || '',
    imageUrl: itemImage(item, documents),
    category: categories[0] || extra.category || undefined,
    publishedAt: item.tarih || item.eklemeTarihi || undefined,
    isFeatured: Boolean(extra.isFeatured) || placements.includes('oneCikan'),
    isPublished: extra.isPublished ?? Boolean(item.aktif),
    createdAt: item.eklemeTarihi || undefined,
    documents,
  };
}

function mapJobItem(item: IcerikItem): Job | undefined {
  if (simplify(item.tip) !== 'isilani') return undefined;
  const extra = parseExtra(item.ekstra);
  return {
    id: item.id,
    title: normalizeText(extra.title) || item.baslik,
    department: extra.department || '',
    location: extra.location || '',
    employmentType: extra.employmentType || '',
    description: normalizeText(item.aciklama) || '',
    requirements: extra.requirements || '',
    applyUrl: item.link || '',
    sortOrder: item.sira ?? 0,
    isPublished: extra.isPublished ?? Boolean(item.aktif),
    createdAt: item.eklemeTarihi || undefined,
  };
}

function mapStatisticItem(item: IcerikItem): Statistic | undefined {
  if (simplify(item.tip) !== 'istatistik') return undefined;
  const extra = parseExtra(item.ekstra);
  return {
    id: item.id,
    category: extra.category || extra.statsCategory || 'genel',
    label: normalizeText(extra.label) || normalizeText(extra.title) || item.baslik,
    value: extra.value || normalizeText(item.link) || '',
    icon: extra.icon || '',
    description: normalizeText(item.aciklama) || '',
    imageUrl: item.gorsel || '',
    sortOrder: item.sira ?? 0,
  };
}

function mapServiceHeroStat(item: IcerikItem): Statistic | undefined {
  if (simplify(item.tip) !== 'hizmetler') return undefined;
  const extra = parseExtra(item.ekstra);
  const isHeroStat =
    simplify(extra.recordType) === 'servicesherostat' ||
    simplify(item.baslik) === 'hizmetler-hero-istatistikleri';
  if (!isHeroStat) return undefined;

  const rawLabel = normalizeText(item.aciklama);
  const label = stripNumberSuffix(item.aciklama);
  const value = normalizeText(item.link) || normalizeText(item.aciklama);
  if (!label || !value) return undefined;

  // Portal satirlari -2700, -2701, -2702 sirasiyla yazar; etiket dile gore degistigi icin
  // sira etiketten degil Sira degerinden cikarilir.
  const sira = item.sira ?? 0;
  const sortOrder = sira <= -2700 && sira > -2800 ? -2700 - sira + 1 : Number(rawLabel.match(/(\d+)\s*$/)?.[1] || sira);

  return {
    id: item.id,
    category: 'hero',
    label,
    value,
    sortOrder,
  };
}

function mapOperationStatPairs(items: IcerikItem[]) {
  const values = new Map<string, { value?: string; label?: string; sortOrder: number; category: string }>();

  items.forEach((item) => {
    if (simplify(item.tip) !== 'operasyon') return;
    const text = simplify(item.baslik);
    const extra = parseExtra(item.ekstra);
    const pageSlug = normalizePageSlug(extra.pageSlug);
    const match = text.match(/kutucuk(?:\s+|-\s*|-)(deger|aciklama)(?:\s+|-\s*|-)(\d+)/);
    if (!match) return;

    const key = match[2];
    const category = pageSlug === 'hizmetler' ? 'services_operations' : 'home_operations';
    const current = values.get(`${category}-${key}`) || { sortOrder: Number(key), category };
    if (match[1] === 'deger') current.value = normalizeText(item.aciklama) || normalizeText(item.link);
    if (match[1] === 'aciklama') current.label = normalizeText(item.aciklama) || normalizeText(item.link);
    values.set(`${category}-${key}`, current);
  });

  return Array.from(values.entries())
    .map(([key, pair]) => ({
      id: key.startsWith('services_operations-') ? 1000 + Number(key.split('-').pop()) : Number(key.split('-').pop()),
      category: pair.category,
      label: pair.label || '',
      value: pair.value || '',
      sortOrder: pair.sortOrder,
    }))
    .filter((item) => item.label && item.value)
    .sort((a, b) => a.sortOrder - b.sortOrder);
}

/** Portal Hizmetler > Operasyonlar Tanitim kutucuklari (operations_intro_stat_N_*) */
function mapServicesOperationsIntroStats(items: IcerikItem[]) {
  const values = new Map<string, { value?: string; label?: string; sortOrder: number }>();

  items.forEach((item) => {
    const extra = parseExtra(item.ekstra);
    if (simplify(extra.recordType) !== 'contentblock') return;
    if (normalizePageSlug(extra.pageSlug) !== 'hizmetler') return;

    const blockKey = extra.blockKey || '';
    if (blockKey === 'operations_intro' && Array.isArray(extra.items)) {
      extra.items.forEach((entry, index) => {
        if (!entry || typeof entry !== 'object' || Array.isArray(entry)) return;
        const current = values.get(String(index + 1)) || { sortOrder: index + 1 };
        const raw = entry as Record<string, unknown>;
        current.value =
          readObjectString(raw.value) ||
          readObjectString(raw.deger) ||
          readObjectString(raw.title);
        current.label =
          readObjectString(raw.label) ||
          readObjectString(raw.aciklama) ||
          readObjectString(raw.description) ||
          readObjectString(raw.subtitle);
        values.set(String(index + 1), current);
      });
      return;
    }

    const match = blockKey.match(/^operations_intro_stat_(\d+)_(value|label)$/);
    if (!match) return;

    const key = match[1];
    const current = values.get(key) || { sortOrder: Number(key) };
    const text = normalizeText(item.aciklama) || normalizeText(item.link);
    if (match[2] === 'value') current.value = text;
    if (match[2] === 'label') current.label = text;
    values.set(key, current);
  });

  return Array.from(values.entries())
    .map(([key, pair]) => ({
      id: 1000 + Number(key),
      category: 'services_operations',
      label: pair.label || '',
      value: pair.value || '',
      sortOrder: pair.sortOrder,
    }))
    .filter((item) => item.label && item.value)
    .sort((a, b) => a.sortOrder - b.sortOrder);
}

function mapCalculatorRate(item: IcerikItem): CalculatorRate | undefined {
  const extra = parseExtra(item.ekstra);
  if (simplify(extra.recordType) !== 'calculatorrate') return undefined;

  return {
    id: item.id,
    rateKey: extra.rateKey || slugify(item.baslik) || `rate-${item.id}`,
    label:
      normalizeText(extra.label) ||
      normalizeText(extra.title) ||
      item.baslik ||
      extra.rateKey ||
      `Tarife ${item.id}`,
    value: normalizeText(item.link) || normalizeText(extra.value) || '0',
    unit: extra.unit || '',
    category: extra.category || '',
    description: extra.description || normalizeText(item.aciklama) || undefined,
    sortOrder: item.sira ?? 0,
  };
}

/**
 * Portal Medya Kutuphanesi > Anasayfa Dokuman Kartlari. Baslik TR anahtaridir; gorunen baslik
 * Aciklama'da (dile gore), alt etiket Ekstra.subLabel'da tutulur. Dosya, grup ve ikon dilden bagimsizdir.
 */
function mapHomeDocument(item: IcerikItem): DocumentItem | undefined {
  const extra = parseExtra(item.ekstra);
  if (simplify(extra.recordType) !== 'homedocument') return undefined;
  if (extra.isPublished === false) return undefined;

  const fileUrl = normalizeText(extra.fileUrl);
  return {
    id: item.id,
    title: normalizeText(item.aciklama) || item.baslik.replace(/^Dokuman Karti\s*-\s*/i, ''),
    category: normalizeText(extra.subLabel) || undefined,
    fileUrl: (fileUrl && mediaUrl(fileUrl)) || '#',
    // Indirme dosya uzantisi icin icerik turu (orn. application/pdf, video/mp4)
    icon: normalizeText(extra.contentType) || undefined,
    sortOrder: item.sira ?? 0,
    isPublished: true,
    group: normalizeText(extra.group) || undefined,
    iconKey: normalizeText(extra.icon) || undefined,
    fileName: normalizeText(extra.fileName) || undefined,
  };
}

function mapContentBlock(item: IcerikItem): ContentBlock | undefined {
  const extra = parseExtra(item.ekstra);
  const documents = mapDocuments(item.dokumanlar);
  const titleSimple = simplify(item.baslik);
  const isOpsIntroByTitle = titleSimple.includes('operasyonlar tanitim');
  const isInfraServicesByTitle = titleSimple.includes('hizmet alanlari');
  const pageSlug = normalizePageSlug(
    extra.pageSlug ||
      (isOpsIntroByTitle ? 'hizmetler' : undefined) ||
      (isInfraServicesByTitle ? 'liman-ozellikleri' : undefined) ||
      item.tip,
  );

  if (!pageSlug) return undefined;

  const isExplicitBlock = simplify(extra.recordType) === 'contentblock';
  const isKnownHomeOperation = simplify(item.tip) === 'operasyon';
  if (!isExplicitBlock && !isKnownHomeOperation && !isOpsIntroByTitle && !isInfraServicesByTitle) {
    return undefined;
  }

  const blockKey = extra.blockKey || inferBlockKey(pageSlug, item.baslik);
  const listItems =
    Array.isArray(extra.items) && extra.items.length > 0
      ? extra.items
      : parseItemList(item.aciklama);

  return {
    id: item.id,
    pageSlug,
    blockKey,
    // EN gibi dillerde cevrilmis baslik Ekstra.title'da tutulur (Baslik TR anahtaridir)
    title: (normalizeText(extra.title) || item.baslik).split(' - ').pop()?.trim() || item.baslik,
    subtitle: extra.subtitle || undefined,
    content: normalizeText(item.aciklama) || normalizeText(item.link) || extra.content || undefined,
    imageUrl: itemImage(item, documents),
    items: listItems,
    documents,
    sortOrder: item.sira ?? 0,
  };
}

function isServiceCatalogNoise(item: IcerikItem): boolean {
  const extra = parseExtra(item.ekstra);
  const recordType = simplify(extra.recordType);
  if (recordType === 'contentblock' || recordType === 'servicesherostat') return true;
  if (extra.blockKey || extra.pageSlug) return true;

  const title = simplify(item.baslik);
  if (title.includes('operasyonlar tanitim')) return true;
  if (title.includes('hizmet alanlari')) return true;
  if (title.includes('hizmetler hero')) return true;
  if (title === 'hizmetler-hero-istatistikleri') return true;
  return false;
}

function mapServiceItem(item: IcerikItem): Service | undefined {
  const extra = parseExtra(item.ekstra);
  const tip = simplify(item.tip);
  // Gercek hizmet katalogu tip=Hizmetler; Operasyon kayitlari content/legacy olabilir
  if (tip !== 'hizmetler') return undefined;
  if (isServiceCatalogNoise(item)) return undefined;

  const documents = mapDocuments(item.dokumanlar);
  return {
    id: item.id,
    // slug Turkce Baslik'tan uretilir (iki dilde ayni), gorunen baslik cevrilebilir
    title: normalizeText(extra.title) || item.baslik,
    slug: extra.slug || slugify(item.baslik),
    category: extra.category || 'operasyon',
    description: normalizeText(item.aciklama) || extra.content || '',
    // API'de gorsel alani yok; portal imageUrl'i ekstra'ya yazar, dokuman da olabilir
    imageUrl: extra.imageUrl || itemImage(item, documents) || undefined,
    features: extra.features || parseItemList(item.link) || [],
    documents,
    sortOrder: item.sira ?? 0,
    isPublished: extra.isPublished ?? Boolean(item.aktif),
  };
}

function mapPageFromItem(
  pages: Map<string, Page>,
  item: IcerikItem,
) {
  const extra = parseExtra(item.ekstra);
  const isPageRecord = simplify(extra.recordType) === 'page';
  const pageSlug = normalizePageSlug(
    isPageRecord
      ? extra.slug || item.baslik
      : extra.pageSlug || extra.slug || item.tip,
  );
  if (!pageSlug || pageSlug === 'sayfa') return;

  if (isPageRecord) {
    const page = ensurePage(pages, pageSlug, item.baslik || pageSlug);
    const docs = mapDocuments(item.dokumanlar);
    // pageSlug Turkce Baslik/slug'dan cozulur; sayfa adi Ekstra.title ile cevrilebilir
    page.title = normalizeText(extra.title) || item.baslik || page.title;
    if (extra.heroTitle) page.heroTitle = extra.heroTitle;
    if (extra.heroSubtitle) page.heroSubtitle = extra.heroSubtitle;
    if (extra.heroImage) page.heroImage = extra.heroImage;
    if (extra.heroCtaText) page.heroCtaText = extra.heroCtaText;
    if (extra.heroCtaLink) page.heroCtaLink = extra.heroCtaLink || item.link || page.heroCtaLink;
    if (itemImage(item, docs)) page.heroImage = itemImage(item, docs);
    if (item.link && !page.heroCtaLink) page.heroCtaLink = item.link;
    if (typeof extra.isPublished === 'boolean') page.isPublished = extra.isPublished;
    if (docs.length > 0) page.documents = docs;
    return;
  }

  const field = pageFieldKey(item.baslik);
  if (!field && !extra.heroTitle && !extra.heroSubtitle && !extra.heroImage) return;

  const page = ensurePage(pages, pageSlug, pageSlug);
  const value = normalizeText(item.aciklama) || normalizeText(item.link);
  const docs = mapDocuments(item.dokumanlar);

  if (field && value) {
    (page as unknown as Record<string, unknown>)[field] = value;
  }

  if (extra.heroTitle) page.heroTitle = extra.heroTitle;
  if (extra.heroSubtitle) page.heroSubtitle = extra.heroSubtitle;
  if (extra.heroImage) page.heroImage = extra.heroImage;
  if (extra.heroCtaText) page.heroCtaText = extra.heroCtaText;
  if (extra.heroCtaLink) page.heroCtaLink = extra.heroCtaLink;
  if (!page.heroImage) page.heroImage = itemImage(item, docs);
  if (docs.length > 0) page.documents = docs;
}

function mapAllContent(items: IcerikItem[]): PublicContent {
  const pages = new Map<string, Page>();
  const settings: Partial<SiteSettings> = { ...mapFooterSettings(items) };
  const news: NewsItem[] = [];
  const services: Service[] = [];
  const statistics: Statistic[] = [];
  const contentBlocks: ContentBlock[] = [];
  const standaloneDocuments: DocumentItem[] = [];
  const managedDocuments: DocumentItem[] = [];
  const jobs: Job[] = [];
  const calculatorRates: CalculatorRate[] = [];

  items
    .filter((item) => item.aktif !== false)
    .sort((a, b) => (a.sira ?? 0) - (b.sira ?? 0))
    .forEach((item) => {
      if (simplify(item.tip) === 'footer') {
        return;
      }

      const homeDocument = mapHomeDocument(item);
      if (homeDocument) {
        managedDocuments.push(homeDocument);
        return;
      }

      mapPageFromItem(pages, item);

      if (simplify(item.tip) === 'haber') {
        const newsItem = mapNewsItem(item);
        // Portalda "Yayinda" isareti kaldirilan haber sitede gosterilmez
        if (newsItem.isPublished !== false) news.push(newsItem);
        return;
      }

      const calculatorRate = mapCalculatorRate(item);
      if (calculatorRate) {
        calculatorRates.push(calculatorRate);
        return;
      }

      const job = mapJobItem(item);
      if (job) {
        jobs.push(job);
        return;
      }

      const statistic = mapStatisticItem(item);
      if (statistic) {
        statistics.push(statistic);
        return;
      }

      const heroStat = mapServiceHeroStat(item);
      if (heroStat) {
        statistics.push(heroStat);
      }

      const service = mapServiceItem(item);
      if (service) {
        services.push(service);
      }

      const block = mapContentBlock(item);
      if (block) {
        contentBlocks.push(block);
      }

      const docs = mapDocuments(item.dokumanlar);
      if (docs.length > 0) {
        const promotedDocs =
          simplify(item.tip) === 'dokuman'
            ? docs
            : docs.filter(shouldPromoteDocument);
        if (promotedDocs.length > 0) {
          standaloneDocuments.push(...promotedDocs);
        }
      }
    });

  statistics.push(...mapOperationStatPairs(items));
  statistics.push(...mapServicesOperationsIntroStats(items));

  return {
    settings: settings as SiteSettings,
    pages: Array.from(pages.values()).sort((a, b) => a.sortOrder - b.sortOrder),
    news: news.sort((a, b) => {
      const aTime = a.publishedAt ? new Date(a.publishedAt).getTime() : 0;
      const bTime = b.publishedAt ? new Date(b.publishedAt).getTime() : 0;
      return bTime - aTime;
    }),
    services: services.sort((a, b) => a.sortOrder - b.sortOrder),
    statistics: dedupeByLatestId(
      statistics,
      (item) => `${item.category}:${item.label}:${item.sortOrder}`,
    ),
    contentBlocks: dedupeByLatestId(
      contentBlocks,
      (item) => `${item.pageSlug}:${item.blockKey}`,
    ),
    managedDocuments: managedDocuments.sort((a, b) => a.sortOrder - b.sortOrder),
    documents: dedupeByLatestId(
      standaloneDocuments,
      (item) => slugify(item.title) || String(item.id),
    ),
    jobs: jobs
      .filter((item) => item.isPublished !== false)
      .sort((a, b) => a.sortOrder - b.sortOrder),
    calculatorRates: calculatorRates.sort((a, b) => a.sortOrder - b.sortOrder),
  };
}

export function fetchPublicContent(options?: ContentQuery) {
  return apiRequest<IcerikItem[]>(contentPath(options)).then(mapAllContent);
}

export function fetchPublicPage(slug: string, options?: ContentQuery) {
  return fetchPublicContent(options).then((content) => ({
    page: content.pages.find((page) => page.slug === slug) as Page,
    contentBlocks: content.contentBlocks.filter((block) => block.pageSlug === slug),
  })) as Promise<PublicPageResponse>;
}

export function fetchPublicNewsList(options?: ContentQuery) {
  return apiRequest<IcerikItem[]>(contentPath(options, { tip: 'Haber' })).then((items) =>
    items
      .map(mapNewsItem)
      .filter((item) => item.isPublished !== false)
      .sort((a, b) => {
        const aTime = a.publishedAt ? new Date(a.publishedAt).getTime() : 0;
        const bTime = b.publishedAt ? new Date(b.publishedAt).getTime() : 0;
        return bTime - aTime;
      }),
  );
}

export function fetchPublicNews(slug: string, options?: ContentQuery) {
  return fetchPublicNewsList(options).then((items) => {
    const found = items.find((item) => item.slug === slug);
    if (!found) throw new Error('Haber bulunamadi');
    return found;
  });
}

/** Hizmet katalogu — Port_Api GET /api/icerikler?tip=Hizmetler&dil=TR|EN */
export function fetchPublicServicesList(options?: ContentQuery) {
  return apiRequest<IcerikItem[]>(contentPath(options, { tip: 'Hizmetler' })).then((items) =>
    items
      .map(mapServiceItem)
      .filter((item): item is Service => Boolean(item))
      .sort((a, b) => a.sortOrder - b.sortOrder),
  );
}

/** Acik pozisyonlar — Port_Api GET /api/icerikler?tip=IsIlani&dil=TR|EN */
export function fetchPublicJobsList(options?: ContentQuery) {
  return apiRequest<IcerikItem[]>(contentPath(options, { tip: 'IsIlani' })).then((items) =>
    items
      .map(mapJobItem)
      .filter((item): item is Job => item != null && item.isPublished !== false)
      .sort((a, b) => a.sortOrder - b.sortOrder),
  );
}

/**
 * Ardiye tarifeleri dilden bagimsizdir; portal bunlari her zaman TR kaydi olarak yazar.
 * Bu yuzden EN sitede de TR kayitlari okunur.
 */
export function fetchPublicCalculatorRates() {
  return apiRequest<IcerikItem[]>(contentPath({ dil: 'TR' }, { tip: 'Sayfa' })).then((items) =>
    items
      .filter((item) => item.aktif !== false)
      .map(mapCalculatorRate)
      .filter((item): item is CalculatorRate => Boolean(item))
      .sort((a, b) => a.sortOrder - b.sortOrder),
  );
}

/** Tek kayit — Port_Api GET /api/icerikler/{id} (dil filtresi yok, kaydin dili yanitta doner). */
export function fetchPublicItem(id: number) {
  return apiRequest<IcerikItem>(`/icerikler/${id}`);
}

/** Anasayfa toplu ucu — Port_Api GET /api/icerikler/anasayfa?dil=TR|EN (ALL kabul etmez). */
export function fetchHomeContent(dil?: Exclude<ContentLanguage, 'ALL'>) {
  const params = new URLSearchParams();
  if (dil) params.set('dil', dil);
  const query = params.toString();
  return apiRequest<IcerikItem[]>(`/icerikler/anasayfa${query ? `?${query}` : ''}`).then(
    mapAllContent,
  );
}

export function mediaUrl(path?: string): string | undefined {
  if (!path) return undefined;
  const trimmed = path.trim();
  if (!trimmed) return undefined;
  // Sadece dosya adi geldiyse (ornek: foto.jpg) gecerli bir medya URL'si degildir
  if (!trimmed.includes('/') && !/^data:/i.test(trimmed) && !/^https?:\/\//i.test(trimmed)) {
    return undefined;
  }
  if (/^data:/i.test(trimmed)) return trimmed;
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  // Webpack ile paketlenen yerel gorseller web origin'inde durur, API'ye yonlendirilmez.
  // homepage "/Web" oldugu icin import edilen gorsel yollari "/Web/static/..." seklindedir.
  const publicUrl = process.env.PUBLIC_URL || '';
  if (trimmed.startsWith('/static/') || (publicUrl && trimmed.startsWith(`${publicUrl}/static/`))) {
    return trimmed;
  }
  // /api/dokumanlar/... ve /uploads/... web origin'inde degil, Port_Api'de
  if (trimmed.startsWith('/api/') || trimmed.startsWith('/uploads')) {
    return `${SERVER_BASE}${trimmed}`;
  }
  if (trimmed.startsWith('/')) {
    return `${SERVER_BASE}${trimmed}`;
  }
  return trimmed;
}
